/**
 * OpenInApp — chip-styled link that opens (or focuses) an app page in
 * the user's other tab. The App Guide is intended to be read in browser
 * tab A while the user navigates the live app in browser tab B; this
 * chip is the bridge.
 *
 * Behaviour:
 *   • If we have a window.opener (we were opened from the app via
 *     target=_blank), we use opener.postMessage so the parent tab
 *     navigates and focuses without losing the guide tab.
 *   • Otherwise we open the URL in a new browser tab.
 */
import React from 'react';
import { Chip } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useAppTheme } from '../../hooks/useAppTheme';

interface Props {
  /** App-relative path, e.g. "/dashboard" or "/strategies#sniper". */
  to: string;
  label: string;
  size?: 'small' | 'medium';
}

const OpenInApp: React.FC<Props> = ({ to, label, size = 'small' }) => {
  const { C } = useAppTheme();

  const handle = (e: React.MouseEvent) => {
    e.preventDefault();
    const url = window.location.origin + to;
    try {
      // Try to focus the opener (the original earngush tab the user came from)
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage({ type: 'earngush:navigate', to }, window.location.origin);
        try { window.opener.focus(); } catch { /* ignore */ }
        return;
      }
    } catch { /* ignore */ }
    // Fall back to a new tab
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Chip
      size={size}
      icon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
      label={label}
      onClick={handle}
      clickable
      sx={{
        bgcolor: C.neutralMuted,
        color: C.neutral,
        border: '1px solid',
        borderColor: C.neutralBorder,
        fontWeight: 600,
        fontSize: '0.72rem',
        '& .MuiChip-icon': { color: C.neutral, ml: 0.5 },
        '&:hover': { bgcolor: C.neutral, color: C.onSolid, '& .MuiChip-icon': { color: '#fff' } },
      }}
    />
  );
};

export default OpenInApp;
